/**
 * Altmängel einer Nachprüfung.
 *
 * Die Nachprüfung hat einen anderen Zweck als die Hauptuntersuchung: Bernd
 * geht nicht den ganzen Platz durch, sondern die Mängel, die beim letzten Mal
 * offen geblieben sind. Für jeden entscheidet er, ob er behoben ist.
 *
 * Warum das Bestätigen nur hier geht und nicht im Portal: wer den Mangel
 * gemeldet hat, darf ihn nicht selbst für erledigt erklären. Der Betreiber
 * setzt auf "gemeldet", auf "behoben" kommt nur der Prüfer – und nur, wenn er
 * dafür vor Ort war. Deshalb hängt die Bestätigung an einer Nachprüfung.
 *
 * Ein bestätigter Mangel hebt zugleich die Sperre des Gerätes auf. Genau
 * dafür ist die Nachprüfung da.
 */

import { useState } from 'react'
import {
  useAltmaengel,
  useBehebungAblehnen,
  useBehebungBestaetigen,
  type Altmangel,
} from '../api/abfragen'
import { Blatt } from '../bausteine/Blatt'
import {
  Fehlerhinweis,
  Ladeanzeige,
  Leerhinweis,
  Stufenzeichen,
  datumDeutsch,
} from '../bausteine/Hinweise'

export function Altmaengel({
  pruefungId,
  istNachpruefung,
}: {
  pruefungId: string
  istNachpruefung: boolean
}) {
  const maengel = useAltmaengel(pruefungId, istNachpruefung)
  const [bearbeitet, setzeBearbeitet] = useState<Altmangel | null>(null)

  if (!istNachpruefung) return null
  if (maengel.isLoading) return <Ladeanzeige text="Lädt offene Mängel …" />
  if (maengel.error) return <Fehlerhinweis fehler={maengel.error} />

  const liste = maengel.data ?? []

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="text-xl font-bold">Offene Mängel der Vorprüfung</h2>
        <span className="text-base text-slate-700">
          {liste.length === 0 ? 'alle erledigt' : `${liste.length} zu klären`}
        </span>
      </div>

      {liste.length === 0 ? (
        <Leerhinweis
          titel="Nichts mehr offen"
          text="Alle Mängel früherer Prüfungen sind bestätigt oder hinfällig."
        />
      ) : (
        <ul className="space-y-2">
          {liste.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                className="karte-knopf items-start"
                onClick={() => setzeBearbeitet(m)}
              >
                <Stufenzeichen stufe={m.stufe} klein />
                <div className="flex-1">
                  <div className="font-semibold">
                    {m.kennung ? `${m.kennung} · ` : ''}
                    {m.geraet_bezeichnung}
                  </div>
                  {m.mangel && <div className="text-base text-slate-800">{m.mangel}</div>}
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <span className="marke bg-slate-100 text-slate-700">
                      seit {datumDeutsch(m.festgestellt_am)}
                    </span>
                    {m.gesperrt && (
                      <span className="marke bg-stufe-3-grund text-stufe-3">gesperrt</span>
                    )}
                    {m.mangelstatus === 'gemeldet' && (
                      <span className="marke bg-stufe-1-grund text-stufe-1">
                        Behebung gemeldet
                      </span>
                    )}
                    {m.frist_am && (
                      <span className="marke bg-slate-100 text-slate-700">
                        Frist {datumDeutsch(m.frist_am)}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {bearbeitet && (
        <Entscheiden
          mangel={bearbeitet}
          pruefungId={pruefungId}
          schliessen={() => setzeBearbeitet(null)}
        />
      )}
    </section>
  )
}

function Entscheiden({
  mangel,
  pruefungId,
  schliessen,
}: {
  mangel: Altmangel
  pruefungId: string
  schliessen: () => void
}) {
  const bestaetigen = useBehebungBestaetigen(pruefungId)
  const ablehnen = useBehebungAblehnen(pruefungId)
  const [bemerkung, setzeBemerkung] = useState('')
  const [grund, setzeGrund] = useState('')
  const [ablehnenOffen, setzeAblehnenOffen] = useState(false)

  const laeuft = bestaetigen.isPending || ablehnen.isPending

  return (
    <Blatt
      titel={mangel.geraet_bezeichnung}
      untertitel={`Festgestellt am ${datumDeutsch(mangel.festgestellt_am)}${
        mangel.berichtsnummer ? ` · Bericht ${mangel.berichtsnummer}` : ''
      }`}
      schliessen={schliessen}
      fuss={
        ablehnenOffen ? (
          <div className="flex gap-3">
            <button
              type="button"
              className="knopf-still flex-1"
              onClick={() => setzeAblehnenOffen(false)}
            >
              Zurück
            </button>
            <button
              type="button"
              className="knopf-warnung flex-1"
              disabled={laeuft || grund.trim() === ''}
              onClick={() =>
                ablehnen.mutate(
                  { befundId: mangel.id, grund: grund.trim() },
                  { onSuccess: schliessen },
                )
              }
            >
              {ablehnen.isPending ? 'Speichert …' : 'Besteht weiter'}
            </button>
          </div>
        ) : (
          <div className="flex gap-3">
            <button
              type="button"
              className="knopf-still flex-1"
              onClick={() => setzeAblehnenOffen(true)}
              disabled={laeuft}
            >
              Besteht weiter
            </button>
            <button
              type="button"
              className="knopf-haupt flex-1"
              disabled={laeuft}
              onClick={() =>
                bestaetigen.mutate(
                  { befundId: mangel.id, bemerkung: bemerkung.trim() || undefined },
                  { onSuccess: schliessen },
                )
              }
            >
              {bestaetigen.isPending ? 'Speichert …' : 'Behoben'}
            </button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <Stufenzeichen stufe={mangel.stufe} />
          <div>
            {mangel.mangel && <p className="font-semibold">{mangel.mangel}</p>}
            {mangel.normverweis && (
              <p className="text-base text-slate-700">{mangel.normverweis}</p>
            )}
          </div>
        </div>

        {mangel.gesperrt && (
          <p className="rounded-feld border-2 border-stufe-3 bg-stufe-3-grund p-3 text-stufe-3">
            Das Gerät ist gesperrt. Mit der Bestätigung wird die Sperre aufgehoben.
          </p>
        )}

        {mangel.gemeldet_am ? (
          <div className="rounded-feld border-2 border-stufe-1 bg-stufe-1-grund p-3">
            <p className="font-semibold text-stufe-1">
              Der Kunde hat am {datumDeutsch(mangel.gemeldet_am)} eine Behebung
              gemeldet.
            </p>
            {mangel.meldungstext && (
              <p className="mt-1 whitespace-pre-line text-slate-800">
                {mangel.meldungstext}
              </p>
            )}
          </div>
        ) : mangel.meldung_erwartet ? (
          <p className="text-base text-slate-700">
            Der Kunde hat keine Behebung gemeldet. Sie können sie trotzdem vor Ort
            feststellen.
          </p>
        ) : (
          /* Der Unterschied ist wichtig für die Nachprüfung: ausbleibende
             Meldung als Versäumnis zu lesen wäre falsch, wenn nie eine
             erwartet wurde. */
          <p className="text-base text-slate-700">
            Für diesen Mangel war <strong>keine Rückmeldung vorgesehen</strong> – bei
            der Beurteilung so festgelegt. Die Behebung ist hier vor Ort
            festzustellen.
          </p>
        )}

        {ablehnenOffen ? (
          <div>
            <label htmlFor="grund">Warum besteht der Mangel weiter?</label>
            <textarea
              id="grund"
              value={grund}
              onChange={(e) => setzeGrund(e.target.value)}
              placeholder="z. B. Schraube weiterhin lose, nur nachgezogen statt ersetzt"
              required
            />
            <p className="mt-1 text-base text-slate-600">
              Der Grund wird an der Meldung festgehalten, und der Mangel steht im Portal
              wieder als offen.
            </p>
          </div>
        ) : (
          <div>
            <label htmlFor="bemerkung">Bemerkung zur Behebung (freiwillig)</label>
            <textarea
              id="bemerkung"
              value={bemerkung}
              onChange={(e) => setzeBemerkung(e.target.value)}
              placeholder="z. B. Schraube ersetzt, Sitz nachgezogen"
            />
          </div>
        )}

        <Fehlerhinweis fehler={bestaetigen.error} />
        <Fehlerhinweis fehler={ablehnen.error} />
      </div>
    </Blatt>
  )
}
